import { RefObject, useEffect } from 'react'

import useMountEffect from './useMountEffect'
import insertContentWithStyles from '../utils/insertHtmlContentWithStyles'

function createShadowEffect(
  containerWithItsOwnStyles: RefObject<HTMLDivElement | null>,
  htmlString?: string,
  onContainerInserted?: () => void,
) {
  if (!containerWithItsOwnStyles.current) return

  // Re-use the shadow root if the element already has one (same DOM node),
  // or create a fresh one if the element was recreated (e.g. after key change).
  if (!containerWithItsOwnStyles.current.shadowRoot) {
    containerWithItsOwnStyles.current.attachShadow({ mode: 'open' })
  }

  const shadowRoot = containerWithItsOwnStyles.current.shadowRoot!
  const html = htmlString || shadowRoot.innerHTML

  insertContentWithStyles(html, shadowRoot).then(onContainerInserted)

  return () => {
    if (!containerWithItsOwnStyles.current) return
    containerWithItsOwnStyles.current.innerHTML = ''
  }
}

/**
 * A hook that allows you to render HTML content with Tailwind CSS styles in an isolated environment using Shadow DOM.
 * This is useful to avoid conflicts with existing styles in the main document.
 *
 * @param html - The HTML string to be rendered inside the shadow DOM.
 * @returns A ref to a container element that will host the shadow DOM with the provided HTML content.
 */
export default function useHtmlWithPlateTwStylesIsolated(
  containerWithItsOwnStyles: RefObject<HTMLDivElement | null>,
  htmlString?: string,
  onContainerInserted?: () => void,
) {
  useMountEffect(() => createShadowEffect(containerWithItsOwnStyles, htmlString, onContainerInserted))

  return containerWithItsOwnStyles
}

export function useHtmlWithPlateTwStylesIsolatedForLastSlide(
  containerWithItsOwnStyles: RefObject<HTMLDivElement | null>,
  htmlString?: string,
  onContainerInserted?: () => void,
) {
  useEffect(() => createShadowEffect(containerWithItsOwnStyles, htmlString, onContainerInserted), [htmlString])

  return containerWithItsOwnStyles
}
